/**
 * AdminDashboard.jsx — Week 3: Unified admin with 4 tabs + Dark Mode + Role Guard
 *
 * ACCESS RULE (matches backend exactly): products_page_enabled:
 *   User must exist in Firestore `systemAdmins` collection
 *   → same check used by getAllTenants, getAllUsers, toggleTenantStatus etc.
 *
 * Tab 1 — Tenants   : table + status toggle, API key, plan selector, business_type dropdown
 * Tab 2 — Users     : table with role/status
 * Tab 3 — Features  : tenant selector + feature flags (auto-save on toggle)
 * Tab 4 — Plans     : full plans management (plan cards + usage)
 *
 * URL hash controls active tab: /admin#tenants | #users | #features | #plans
 */
import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { functions, db } from "../../firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiHome, FiUsers, FiToggleRight, FiZap,
  FiShield, FiRefreshCw, FiPause, FiPlay,
  FiKey, FiCopy, FiCheck, FiAlertCircle,
  FiChevronDown, FiCheckCircle,
  FiEdit2, FiX, FiBarChart2, FiLock,
  FiPlus, FiTrash2, FiUserPlus, FiUserX,
  FiCreditCard, FiEyeOff,
} from "react-icons/fi";
import Navigation from "../../components/layout/Navigation";
import FeatureFlagToggle from "../../components/admin/FeatureFlagToggle";
import TenantFormModal   from "../../components/admin/TenantFormModal";
import DeleteTenantDialog from "../../components/admin/DeleteTenantDialog";
import Toggle from "../../components/admin/Toggle";
import { invalidateSystemSettings } from "../../hooks/usesystemsettings";
import { normaliseRole, ROLE_META } from "../../hooks/useRoles";
import PlanCard from "../../components/plans/PlanCard";
import UsageProgress from "../../components/plans/UsageProgress";
import { useDarkMode } from "../../hooks/useDarkMode";

/* ── Toast ───────────────────────────────────────────────── */
const Toast = ({ msg, type = "success", onDone }) => {
  useEffect(() => { const id = setTimeout(onDone, 2800); return () => clearTimeout(id); }, [onDone]);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-xl border shadow-2xl text-sm font-medium
        ${type === "success" ? "bg-green-900/80 border-green-500/50 text-green-300" : "bg-red-900/80 border-red-500/50 text-red-300"}`}>
      {type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}{msg}
    </motion.div>
  );
};

/* ── Constants ───────────────────────────────────────────── */
const TABS = [
  { id: "tenants",  labelKey: "tenants",      icon: <FiHome /> },
  { id: "users",    labelKey: "users",         icon: <FiUsers /> },
  { id: "features", labelKey: "feature_flags", icon: <FiToggleRight /> },
  { id: "plans",    labelKey: "plans_title",   icon: <FiZap /> },
];

const PLAN_OPTIONS = ["plan_trial", "plan_basic", "plan_pro", "plan_enterprise"];
// Plan name display map. Keys must match the planId values written by
// the backend (createTenant + updateTenantPlan). The legacy plan_*
// aliases are kept so any tenant document still carrying an old id from
// before the cleanup keeps rendering correctly.
const PLAN_NAMES   = {
  free:            "Free",
  pro:             "Pro",
  enterprise:      "Enterprise",
  // Legacy aliases:
  plan_trial:      "Free",
  plan_basic:      "Free",
  plan_pro:        "Pro",
  plan_enterprise: "Enterprise",
};
const BIZ_TYPES    = ["agency", "store", "service"];
const BIZ_ICONS    = { agency: "🎯", store: "🛒", service: "💼" };

const DEFAULT_FEATURES = {
  products_page_enabled: false, campaign_decision_enabled: true, share_enabled: true, pdf_enabled: false,
  decision_mode_enabled: true, allowed_modes: ["conservative", "balanced", "aggressive"],
  languages_enabled: ["en"], max_users: 5,
  analytics_enabled: false, api_access_enabled: false, export_enabled: false, custom_branding_enabled: false,
};

/* ═══════════════════════════════════════════════════════════
   Main component
═══════════════════════════════════════════════════════════ */
const AdminDashboard = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t, isRTL, lang } = useLanguage();
  const { currentUser, userData } = useAuth();
  const { darkMode } = useDarkMode();

  const hashTab   = location.hash.replace("#", "") || "tenants";
  const activeTab = TABS.find(tb => tb.id === hashTab) ? hashTab : "tenants";
  const setActiveTab = (id) => navigate(`/admin#${id}`, { replace: true });

  /* ─────────────────────────────────────────────────────────
     systemAdmin check — mirrors the backend exactly:
       db.collection("systemAdmins").doc(userId).get()
     Three states:
       null  = still checking (show spinner)
       false = checked, not admin (show wall)
       true  = checked, is admin (show dashboard)
  ───────────────────────────────────────────────────────── */
  const [isSystemAdmin, setIsSystemAdmin] = useState(null); // null = loading

  useEffect(() => {
    if (!currentUser?.uid) return;
    // Check the systemAdmins collection — exactly like the backend
    getDoc(doc(db, "systemAdmins", currentUser.uid))
      .then(snap => setIsSystemAdmin(snap.exists()))
      .catch(() => setIsSystemAdmin(false));
  }, [currentUser?.uid]);

  /* ── Data state ─────────────────────────────────────────── */
  const [tenants,       setTenants]       = useState([]);
  const [users,         setUsers]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast,         setToast]         = useState(null);
  const [copiedKey,     setCopiedKey]     = useState(null);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [features,       setFeatures]       = useState(DEFAULT_FEATURES);
  const [featLoading,    setFeatLoading]    = useState(false);
  const [savingFlag,     setSavingFlag]     = useState(null);

  const [plans,        setPlans]        = useState([]);
  const [currentPlan,  setCurrentPlan]  = useState(null);
  const [planUsage,    setPlanUsage]    = useState(null);
  const [planLoading,  setPlanLoading]  = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);

  const [tenantUsageMap,  setTenantUsageMap]  = useState({});
  const [usageLoadingFor, setUsageLoadingFor] = useState(null);
  const [expandedTenants, setExpandedTenants] = useState(new Set());
  const [editingBizType,  setEditingBizType]  = useState(null);

  // Tenant CRUD modal state. `formMode` is "create" or "edit"; in edit
  // mode `targetTenant` is the row to edit. `deleteTarget` is a tenant
  // pending deletion (driving the delete confirm dialog).
  const [formOpen,      setFormOpen]      = useState(false);
  const [formTenant,    setFormTenant]    = useState(null); // null = create mode
  const [deleteTarget,  setDeleteTarget]  = useState(null);

  // Global signup toggle. The system admin can flip this from the
  // landing area; the public /register route redirects silently to
  // /login when it's off, and the LoginPage hides the register tab.
  // Backend createTenant enforces the same flag — toggling it off is
  // genuine, not just a UI gate.
  const [signupEnabled,    setSignupEnabled]    = useState(true);
  const [signupTogglingNow, setSignupTogglingNow] = useState(false);

  // Plans/pricing tab visibility platform-wide. When off, every
  // dashboard hides the "الخطط والأسعار" tab — both for tenant
  // owners (CompanyAdminDashboard) and the system admin's plans
  // section. Useful when billing is paused or invite-only.
  const [plansTabEnabled,    setPlansTabEnabled]    = useState(true);
  const [plansTogglingNow,   setPlansTogglingNow]   = useState(false);

  const showToast = (msg, type = "success") => setToast({ msg, type, id: Date.now() });

  /* ── Style helpers ───────────────────────────────────────── */
  const card       = `border rounded-2xl ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200 shadow-sm"}`;
  const divider    = darkMode ? "border-gray-700"  : "border-gray-200";
  const theadBg    = darkMode ? "bg-gray-800/80"   : "bg-gray-50";
  const rowHover   = darkMode ? "border-gray-700/60 hover:bg-gray-700/20" : "border-gray-200 hover:bg-gray-50";
  const headingCls = darkMode ? "text-white"       : "text-gray-900";
  const subCls     = darkMode ? "text-gray-400"    : "text-gray-500";
  const textCls    = darkMode ? "text-gray-300"    : "text-gray-700";
  const thCls      = `p-4 font-medium text-sm text-left ${darkMode ? "text-gray-400" : "text-gray-500"}`;
  const inputCls   = `w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 ${darkMode ? "bg-gray-700/50 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"}`;
  const refreshBtn = `p-2 rounded-xl transition-colors ${darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`;

  /* ── fetchData ───────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    if (!isSystemAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [tRes, uRes, sRes] = await Promise.all([
        httpsCallable(functions, "getAllTenants")(),
        httpsCallable(functions, "getAllUsers")(),
        // Pull the global system settings alongside tenants/users so
        // the signup toggle's initial state is correct on first paint.
        // Don't fail the whole fetch if this one errors — fall back to
        // the open-by-default behaviour.
        httpsCallable(functions, "getSystemSettings")({}).catch(() => null),
      ]);
      setTenants(tRes.data || []);
      setUsers(uRes.data || []);
      if (sRes?.data?.settings) {
        setSignupEnabled(sRes.data.settings.signupEnabled !== false);
        setPlansTabEnabled(sRes.data.settings.plansTabEnabled !== false);
      }
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setLoading(false); }
  }, [isSystemAdmin]);

  /* ── Toggle public signup ────────────────────────────────────
     Optimistic update — flip the local state first, then call the
     backend. If the backend rejects, revert. This keeps the toggle
     feeling instant. The backend is the source of truth: createTenant
     itself checks systemSettings/global before letting a self-signup
     proceed, so the toggle isn't just a frontend illusion. */
  const handleToggleSignup = async () => {
    if (signupTogglingNow) return;
    const next = !signupEnabled;
    setSignupEnabled(next);
    setSignupTogglingNow(true);
    try {
      const fn = httpsCallable(functions, "updateSystemSettings");
      const res = await fn({ settings: { signupEnabled: next } });
      // Reflect canonical server state in case anything else changed
      // (defensive — currently we only write one flag).
      if (res.data?.settings) {
        setSignupEnabled(res.data.settings.signupEnabled !== false);
      }
      // Invalidate the module-level cache used by useSystemSettings so
      // any other tab/page (e.g. an open login screen) gets fresh data
      // on its next read instead of the stale value.
      invalidateSystemSettings();
      showToast(
        next
          ? (lang === "ar" ? "تم تفعيل التسجيل العام" : "Public signup enabled")
          : (lang === "ar" ? "تم إيقاف التسجيل العام" : "Public signup disabled")
      );
    } catch (err) {
      // Rollback on failure so the UI never lies about the saved state
      setSignupEnabled(!next);
      showToast(err.message || t("error"), "error");
    } finally {
      setSignupTogglingNow(false);
    }
  };

  /* ── Toggle plans/pricing tab visibility ────────────────────
     Mirrors handleToggleSignup. Same optimistic-update + rollback
     pattern. Off → every dashboard hides the "الخطط والأسعار" tab
     and falls back to a non-plans tab if the user is currently on
     it. The flag also feeds the LoginPage / /register flow but is
     primarily an in-app gate, so we just invalidate the cache and
     let mounted dashboards re-render via the useSystemSettings
     hook on their next read. */
  const handleTogglePlansTab = async () => {
    if (plansTogglingNow) return;
    const next = !plansTabEnabled;
    setPlansTabEnabled(next);
    setPlansTogglingNow(true);
    try {
      const fn = httpsCallable(functions, "updateSystemSettings");
      const res = await fn({ settings: { plansTabEnabled: next } });
      if (res.data?.settings) {
        setPlansTabEnabled(res.data.settings.plansTabEnabled !== false);
      }
      invalidateSystemSettings();
      showToast(
        next
          ? (lang === "ar" ? "تم إظهار تبويب الخطط" : "Plans tab visible")
          : (lang === "ar" ? "تم إخفاء تبويب الخطط" : "Plans tab hidden")
      );
    } catch (err) {
      setPlansTabEnabled(!next);
      showToast(err.message || t("error"), "error");
    } finally {
      setPlansTogglingNow(false);
    }
  };

  /* ── loadPlansData ───────────────────────────────────────────
     Defined here (above the useEffect that calls it) so the call
     site is in scope on first render. The previous shape declared
     this further down with `const` — under React 18 strict mode
     the activeTab effect fired before reaching the declaration,
     hitting the temporal dead zone:
       "Cannot access 'loadPlansData' before initialization"
     useCallback also keeps the function reference stable across
     renders so it's safe to put in dependency arrays.            */
  const loadPlansData = useCallback(async () => {
    setPlanLoading(true);
    try {
      // System admins don't belong to a tenant — they admin the whole
      // system. Calling getTenantUsageStats({tenantId: undefined}) would
      // throw, so we only request usage when we have a tenantId.
      // Promise.allSettled keeps a failure in one call from blanking
      // the other (we only care about the plan list here for system
      // admins).
      const calls = [httpsCallable(functions, "getAvailablePlans")()];
      if (currentUser?.tenantId) {
        calls.push(httpsCallable(functions, "getTenantUsageStats")({ tenantId: currentUser.tenantId }));
      }
      const results = await Promise.allSettled(calls);

      const pSettled = results[0];
      if (pSettled.status === "fulfilled" && pSettled.value.data?.success) {
        setPlans(pSettled.value.data.plans);
      } else if (pSettled.status === "rejected") {
        showToast(pSettled.reason?.message || t("error"), "error");
      }

      const uSettled = results[1];
      if (uSettled?.status === "fulfilled" && uSettled.value.data?.success) {
        setPlanUsage(uSettled.value.data.usage);
        setCurrentPlan(uSettled.value.data.plan);
      } else if (uSettled?.status === "rejected") {
        // Don't toast — for system admins this is expected (we skipped
        // the call), and for tenant owners the toast comes from the
        // CompanyAdminDashboard which has a richer error UI.
        // eslint-disable-next-line no-console
        console.warn("getTenantUsageStats:", uSettled.reason?.message);
      }
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setPlanLoading(false); }
  }, [currentUser?.tenantId, t]);

  useEffect(() => {
    if (isSystemAdmin === true) fetchData();
    if (isSystemAdmin === false) setLoading(false);
  }, [isSystemAdmin]);

  useEffect(() => {
    if (activeTab === "plans" && plans.length === 0 && !planLoading && isSystemAdmin) loadPlansData();
    // loadPlansData is intentionally omitted from deps — including it
    // would re-trigger the load every time currentUser?.tenantId
    // changes (it shouldn't, but guarding anyway). The check
    // `plans.length === 0 && !planLoading` already debounces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isSystemAdmin]);

  // If the admin disables the plans tab while it's the active one,
  // bounce to the tenants tab. Otherwise the page would render nothing
  // (the filtered TABS list excludes #plans, but the URL hash still
  // says #plans so no panel matches activeTab).
  useEffect(() => {
    if (!plansTabEnabled && activeTab === "plans") {
      navigate("/admin#tenants", { replace: true });
    }
  }, [plansTabEnabled, activeTab, navigate]);

  /* ══════════════════════════════════════════════════════════
     ① Still checking systemAdmins collection → spinner
  ══════════════════════════════════════════════════════════ */
  if (isSystemAdmin === null || !currentUser) {
    return (
      <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}>
        <Navigation />
        <div className="flex items-center justify-center h-64">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
            <FiRefreshCw className="text-blue-400 text-4xl" />
          </motion.div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════
     ② Not in systemAdmins → ACCESS DENIED wall (EN + AR)
  ══════════════════════════════════════════════════════════ */
  if (!isSystemAdmin) {
    return (
      <div className={`min-h-screen ${darkMode
        ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black"
        : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}
      >
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 25 }}
            className={`w-full max-w-md text-center border rounded-2xl p-10 shadow-2xl
              ${darkMode ? "bg-gray-800/60 border-gray-700" : "bg-white border-gray-200"}`}
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${darkMode ? "bg-red-500/15" : "bg-red-50"}`}>
                <FiLock className="text-red-400 text-4xl" />
              </div>
            </div>

            {/* Title */}
            <h1 className={`text-2xl font-bold mb-3 ${headingCls}`}>
              {lang === "ar" ? "الوصول مقيّد" : "Admin Access Only"}
            </h1>

            {/* Body */}
            <p className={`text-sm leading-relaxed mb-6 ${subCls}`}>
              {lang === "ar"
                ? "هذه الصفحة مخصصة لمشرفي النظام فقط. ليس لديك صلاحية الوصول إلى لوحة تحكم الإدارة."
                : "This page is restricted to system administrators only. You don't have permission to access the admin dashboard."}
            </p>

            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium mb-8 ${darkMode ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
              <FiShield className="text-xs" />
              {lang === "ar" ? "مطلوب: مشرف النظام" : "Required: System Admin"}
            </div>

            {/* CTA */}
            <button onClick={() => navigate("/company")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-sm transition-colors">
              {lang === "ar" ? "العودة إلى لوحة التحكم" : "Back to Dashboard"}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════
     ③ Is system admin, data still loading → spinner
  ══════════════════════════════════════════════════════════ */
  if (loading) return (
    <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}>
      <Navigation />
      <div className="flex items-center justify-center h-64">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <FiRefreshCw className="text-blue-400 text-4xl" />
        </motion.div>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     ④ ADMIN ACTIONS
  ══════════════════════════════════════════════════════════ */
  const handleToggleStatus = async (tenantId) => {
    setActionLoading(tenantId);
    try {
      await httpsCallable(functions, "toggleTenantStatus")({ tenantId });
      await fetchData(); showToast(t("status_updated"));
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setActionLoading(null); }
  };

  const handleRegenerateKey = async (tenantId) => {
    if (!window.confirm(lang === "ar" ? "هل أنت متأكد من إعادة توليد مفتاح API؟" : "Regenerate this tenant's API key?")) return;
    setActionLoading(`key-${tenantId}`);
    try {
      await httpsCallable(functions, "regenerateApiKey")({ tenantId });
      await fetchData(); showToast(t("key_regenerated"));
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setActionLoading(null); }
  };

  const handleChangePlan = async (tenantId, currentPlanId) => {
    const choice = window.prompt(`${t("current_plan")}: ${PLAN_NAMES[currentPlanId] || currentPlanId}\n\nEnter new plan ID:\n${PLAN_OPTIONS.join(" | ")}`);
    if (!choice || !PLAN_OPTIONS.includes(choice.trim())) return;
    setActionLoading(`plan-${tenantId}`);
    try {
      await httpsCallable(functions, "updateTenantPlan")({ tenantId, newPlanId: choice.trim(), oldPlanId: currentPlanId || "plan_trial" });
      await fetchData(); showToast(`${t("plan_upgraded_success")} ${PLAN_NAMES[choice.trim()]}!`);
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setActionLoading(null); }
  };

  const handleChangeBizType = async (tenantId, newBizType) => {
    setActionLoading(`biz-${tenantId}`);
    try {
      // Use updateTenantSettings — it already accepts businessType +
      // validates it via Validator.businessType. The previous
      // updateTenantBusinessType call was a phantom: that function was
      // never implemented on the backend, so the dropdown silently
      // failed every save (Firebase Functions returns 404, browser
      // surfaces it as a CORS preflight error, frontend toasted
      // "internal" and ate the change).
      await httpsCallable(functions, "updateTenantSettings")({
        tenantId,
        updates: { businessType: newBizType },
      });
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, businessType: newBizType } : t));
      setEditingBizType(null);
      showToast(lang === "ar" ? "تم تحديث نوع العمل" : "Business type updated");
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setActionLoading(null); }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const loadTenantUsage = async (tenantId) => {
    if (tenantUsageMap[tenantId] || usageLoadingFor === tenantId) return;
    setUsageLoadingFor(tenantId);
    try {
      const res = await httpsCallable(functions, "getTenantUsageStats")({ tenantId });
      if (res.data.success) setTenantUsageMap(prev => ({ ...prev, [tenantId]: { usage: res.data.usage, plan: res.data.plan } }));
    } catch {}
    finally { setUsageLoadingFor(null); }
  };

  const toggleTenantUsage = async (tenantId) => {
    const next = new Set(expandedTenants);
    if (next.has(tenantId)) { next.delete(tenantId); } else { next.add(tenantId); loadTenantUsage(tenantId); }
    setExpandedTenants(next);
  };

  const loadTenantFeatures = async (tenantId) => {
    setFeatLoading(true);
    try {
      const res = await httpsCallable(functions, "getTenantFeatures")({ tenantId });
      setFeatures(res.data.success ? (res.data.features || DEFAULT_FEATURES) : DEFAULT_FEATURES);
    } catch { setFeatures(DEFAULT_FEATURES); }
    finally { setFeatLoading(false); }
  };

  const handleSelectFeatureTenant = (tenant) => { setSelectedTenant(tenant); loadTenantFeatures(tenant.id); };

  const autoSaveFlag = async (name, newFeatures) => {
    if (!selectedTenant) return;
    setSavingFlag(name);
    try {
      const res = await httpsCallable(functions, "updateTenantFeatures")({ tenantId: selectedTenant.id, features: newFeatures });
      if (res.data.success) showToast(lang === "ar" ? "تم الحفظ" : "Saved");
      else showToast(res.data.error || t("error"), "error");
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setSavingFlag(null); }
  };

  const handleToggleFeature = (name, value) => { const next = { ...features, [name]: value }; setFeatures(next); autoSaveFlag(name, next); };
  const handleArrayFeature   = (name, value) => { const next = { ...features, [name]: value }; setFeatures(next); autoSaveFlag(name, next); };
  const handleMaxUsers = (value) => {
    const next = { ...features, max_users: parseInt(value) };
    setFeatures(next);
    clearTimeout(handleMaxUsers._timer);
    handleMaxUsers._timer = setTimeout(() => autoSaveFlag("max_users", next), 800);
  };

  const handleSelectPlan = async (planId) => {
    if (planId === currentPlan?.id) return;
    if (!window.confirm(`${t("confirm_plan_change")} ${planId.toUpperCase()}?`)) return;
    setUpdatingPlan(true);
    try {
      const res = await httpsCallable(functions, "updateTenantPlan")({ tenantId: currentUser?.tenantId, newPlanId: planId, oldPlanId: currentPlan?.id });
      if (res.data.success) { showToast(`${t("plan_upgraded_success")} ${res.data.plan.name}!`); setCurrentPlan(res.data.plan); await loadPlansData(); }
    } catch (err) { showToast(err.message || t("error"), "error"); }
    finally { setUpdatingPlan(false); }
  };

  /* ══════════════════════════════════════════════════════════
     ⑤ FULL ADMIN RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div dir={isRTL ? "rtl" : "ltr"} className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}>
      <Navigation />

      <AnimatePresence>
        {toast && <Toast key={toast.id} msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      </AnimatePresence>

      <div className="p-4 md:p-8 max-w-7xl mx-auto">

        {/* Page header */}
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-8">
          <div className="flex items-center gap-3">
            <FiShield className="text-3xl text-red-400" />
            <div className="text-left">
              <h1 className={`text-2xl md:text-3xl font-bold ${headingCls}`}>{t("admin_title")}</h1>
              <p className={`${subCls} text-sm`}>NexusFlow Management</p>
            </div>
          </div>
        </motion.div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: <FiHome className="text-blue-400" />,    bg: "bg-blue-500/20",   label: t("total_tenants"), val: tenants.length },
            { icon: <FiUsers className="text-green-400" />,  bg: "bg-green-500/20",  label: t("total_users"),   val: users.length },
            { icon: <FiPause className="text-yellow-400" />, bg: "bg-yellow-500/20", label: t("suspended"),     val: tenants.filter(t => t.status === "suspended").length },
            { icon: <FiZap className="text-purple-400" />,   bg: "bg-purple-500/20", label: lang === "ar" ? "خطط نشطة" : "Active Plans", val: tenants.filter(t => t.planId && t.planId !== "plan_trial").length },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className={`${card} p-5`}>
              <div className="flex items-center gap-3">
                <div className={`p-2.5 ${s.bg} rounded-xl`}>{s.icon}</div>
                <div className="text-left">
                  <p className={`${subCls} text-xs`}>{s.label}</p>
                  <p className={`text-2xl font-bold ${headingCls}`}>{s.val}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── Public signup toggle ──────────────────────────
            System admin can switch self-signup on/off here. When
            off, the public /register route silently redirects to
            /login and the LoginPage hides the register tab. The
            backend createTenant function enforces the same flag.
            Mounted between the stats row and the tabs so it's
            visible on every tab without crowding any of them. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className={`${card} p-4 mb-3 flex items-center justify-between gap-4`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2.5 rounded-xl ${signupEnabled ? "bg-blue-500/15 text-blue-400" : "bg-gray-500/15 text-gray-400"}`}>
              {signupEnabled ? <FiUserPlus /> : <FiUserX />}
            </div>
            <div className="min-w-0">
              <p className={`font-semibold ${headingCls} text-sm truncate`}>
                {lang === "ar" ? "صفحة التسجيل العامة" : "Public signup page"}
              </p>
              <p className={`${subCls} text-xs truncate`}>
                {signupEnabled
                  ? (lang === "ar"
                      ? "أي شخص يمكنه إنشاء حساب من /register"
                      : "Anyone can create an account at /register")
                  : (lang === "ar"
                      ? "التسجيل بدعوة فقط — أنشئ الحسابات من \"+ إضافة شركة\""
                      : "Invite-only — create accounts from \"+ Add Company\"")}
              </p>
            </div>
          </div>
          <Toggle
            checked={signupEnabled}
            onChange={handleToggleSignup}
            disabled={signupTogglingNow}
            ariaLabel={lang === "ar" ? "تفعيل التسجيل العام" : "Enable public signup"}
            title={
              signupEnabled
                ? (lang === "ar" ? "إيقاف التسجيل العام" : "Disable public signup")
                : (lang === "ar" ? "تفعيل التسجيل العام" : "Enable public signup")
            }
          />
        </motion.div>

        {/* ── Plans tab visibility toggle ───────────────────
            Hides "الخطط والأسعار" tab platform-wide. Useful when
            billing is being reworked or the product is invite-only —
            we want to keep tenants from seeing pricing they can't
            actually act on. The flag is read by every dashboard via
            the useSystemSettings hook. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className={`${card} p-4 mb-6 flex items-center justify-between gap-4`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2.5 rounded-xl ${plansTabEnabled ? "bg-blue-500/15 text-blue-400" : "bg-gray-500/15 text-gray-400"}`}>
              {plansTabEnabled ? <FiCreditCard /> : <FiEyeOff />}
            </div>
            <div className="min-w-0">
              <p className={`font-semibold ${headingCls} text-sm truncate`}>
                {lang === "ar" ? "تبويب الخطط والأسعار" : "Plans & pricing tab"}
              </p>
              <p className={`${subCls} text-xs truncate`}>
                {plansTabEnabled
                  ? (lang === "ar"
                      ? "تبويب \"الخطط والأسعار\" ظاهر لجميع المستخدمين"
                      : "\"Plans & pricing\" tab is visible to all users")
                  : (lang === "ar"
                      ? "التبويب مخفي — لا يرى المستخدمون أي معلومات عن الخطط"
                      : "Tab is hidden — users don't see any plan info")}
              </p>
            </div>
          </div>
          <Toggle
            checked={plansTabEnabled}
            onChange={handleTogglePlansTab}
            disabled={plansTogglingNow}
            ariaLabel={lang === "ar" ? "إظهار تبويب الخطط" : "Show plans tab"}
            title={
              plansTabEnabled
                ? (lang === "ar" ? "إخفاء تبويب الخطط" : "Hide plans tab")
                : (lang === "ar" ? "إظهار تبويب الخطط" : "Show plans tab")
            }
          />
        </motion.div>

        {/* Tabs — filtered against plansTabEnabled so the system
            admin's "Plans" tab disappears when the platform-wide
            toggle is off, mirroring the tenant-side behaviour. The
            toggle card above stays visible regardless so the admin
            can always re-enable. */}
        <div className={`flex gap-1 ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-1 mb-6 overflow-x-auto`}>
          {TABS.filter(tb => tb.id !== "plans" || plansTabEnabled).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap flex-1 justify-center ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-lg"
                  : darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700/60" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}>
              {tab.icon}<span>{t(tab.labelKey)}</span>
            </button>
          ))}
        </div>

        {/* ══ TAB: TENANTS ══ */}
        {activeTab === "tenants" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`${card} overflow-hidden`}>
            <div className={`flex items-center justify-between p-5 border-b ${divider}`}>
              <h2 className={`font-semibold ${headingCls}`}>{t("tenants")} ({tenants.length})</h2>
              <div className="flex items-center gap-2">
                {/* Open the form modal in CREATE mode by passing
                    formTenant=null. The same modal is reused for edit
                    via the per-row pencil button below. */}
                <button
                  onClick={() => { setFormTenant(null); setFormOpen(true); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                >
                  <FiPlus className="text-sm" />
                  <span>{t("admin_add_tenant")}</span>
                </button>
                <button onClick={fetchData} className={refreshBtn}><FiRefreshCw className="text-sm" /></button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theadBg}>
                  <tr>
                    <th className={thCls}>{t("col_company")}</th>
                    <th className={thCls}>{lang === "ar" ? "نوع العمل" : "Biz Type"}</th>
                    <th className={thCls}>Plan</th>
                    <th className={thCls}>{t("col_status")}</th>
                    <th className={thCls}>{t("users")}</th>
                    <th className={thCls}>API Key</th>
                    <th className={thCls}>{lang === "ar" ? "الاستخدام" : "Usage"}</th>
                    <th className={thCls}>{t("col_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(tenant => (
                    // Keyed Fragment — `<>` doesn't accept props so
                    // the key on the inner <tr> wasn't satisfying
                    // React's reconciler ("Each child in a list
                    // should have a unique 'key' prop"). Switching
                    // to <Fragment key=...> silences the warning and
                    // lets React track each row pair (main row + the
                    // optional usage row) as a single keyed unit.
                    <Fragment key={tenant.id}>
                      <tr className={`border-t ${rowHover} transition-colors`}>
                        <td className="p-4">
                          <p className={`font-medium ${headingCls}`}>{tenant.name || tenant.companyName}</p>
                          <p className={`${subCls} text-xs`}>{tenant.slug || tenant.id?.slice(0, 8)}</p>
                        </td>
                        <td className="p-4">
                          {editingBizType === tenant.id ? (
                            <div className="flex items-center gap-1">
                              <select defaultValue={tenant.businessType || "agency"} onChange={e => handleChangeBizType(tenant.id, e.target.value)}
                                className={`border rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"}`}>
                                {BIZ_TYPES.map(bt => <option key={bt} value={bt}>{BIZ_ICONS[bt]} {bt}</option>)}
                              </select>
                              <button onClick={() => setEditingBizType(null)} className={`p-1 rounded ${darkMode ? "text-gray-500 hover:text-white" : "text-gray-400 hover:text-gray-900"}`}><FiX className="text-xs" /></button>
                            </div>
                          ) : (
                            <button onClick={() => setEditingBizType(tenant.id)}
                              className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-lg transition-colors ${darkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`}>
                              {/* Render the actual stored value. If the
                                  field is missing we show "—" rather
                                  than silently labeling every tenant as
                                  "agency". */}
                              {tenant.businessType ? (
                                <>
                                  <span>{BIZ_ICONS[tenant.businessType] || ""}</span>
                                  <span className="capitalize">{tenant.businessType}</span>
                                </>
                              ) : (
                                <span className={subCls}>—</span>
                              )}
                              <FiEdit2 className={`text-xs ${subCls}`} />
                            </button>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`${textCls} text-sm`}>
                            {/* Plan display: read PLAN_NAMES against the
                                canonical fields. The previous fallback
                                to `tenant.subscriptionTier` was checking
                                a field that never gets written. */}
                            {PLAN_NAMES[tenant.planId] || PLAN_NAMES[tenant.plan] || "—"}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${tenant.status === "active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                            {tenant.status === "active" ? t("status_active") : t("suspended")}
                          </span>
                        </td>
                        <td className={`p-4 ${textCls}`}>{tenant.userCount ?? "—"}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <code className={`px-2 py-1 rounded text-xs max-w-[120px] truncate block ${darkMode ? "bg-gray-900 text-gray-400" : "bg-gray-100 text-gray-600"}`}>{tenant.apiKey || "—"}</code>
                            {tenant.apiKey && (
                              <button onClick={() => copyToClipboard(tenant.apiKey, tenant.id)} className={`p-1.5 rounded-lg transition-colors ${darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"}`}>
                                {copiedKey === tenant.id ? <FiCheck className="text-green-400 text-xs" /> : <FiCopy className={`text-xs ${subCls}`} />}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <button onClick={() => toggleTenantUsage(tenant.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                              expandedTenants.has(tenant.id) ? "bg-purple-600 border-purple-500 text-white" : darkMode ? "border-gray-600 text-gray-400 hover:border-purple-500 hover:text-purple-300" : "border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-500"
                            }`}>
                            {usageLoadingFor === tenant.id ? <FiRefreshCw className="animate-spin text-xs" /> : <FiBarChart2 className="text-xs" />}
                            <FiChevronDown className={`text-xs transition-transform ${expandedTenants.has(tenant.id) ? "rotate-180" : ""}`} />
                          </button>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => handleToggleStatus(tenant.id)} disabled={actionLoading === tenant.id}
                              className={`p-2 rounded-xl transition-colors ${tenant.status === "active" ? "bg-red-500/15 hover:bg-red-500/30 text-red-400" : "bg-green-500/15 hover:bg-green-500/30 text-green-400"}`}>
                              {actionLoading === tenant.id ? <FiRefreshCw className="animate-spin text-xs" /> : tenant.status === "active" ? <FiPause className="text-xs" /> : <FiPlay className="text-xs" />}
                            </button>
                            <button onClick={() => handleRegenerateKey(tenant.id)} disabled={actionLoading === `key-${tenant.id}`} className="p-2 bg-yellow-500/15 hover:bg-yellow-500/30 text-yellow-400 rounded-xl transition-colors">
                              {actionLoading === `key-${tenant.id}` ? <FiRefreshCw className="animate-spin text-xs" /> : <FiKey className="text-xs" />}
                            </button>
                            <button onClick={() => handleChangePlan(tenant.id, tenant.planId)} disabled={actionLoading === `plan-${tenant.id}`} className="p-2 bg-purple-500/15 hover:bg-purple-500/30 text-purple-400 rounded-xl transition-colors">
                              {actionLoading === `plan-${tenant.id}` ? <FiRefreshCw className="animate-spin text-xs" /> : <FiZap className="text-xs" />}
                            </button>
                            <button onClick={() => { handleSelectFeatureTenant(tenant); setActiveTab("features"); }} className="p-2 bg-blue-500/15 hover:bg-blue-500/30 text-blue-400 rounded-xl transition-colors" title={t("feature_flags")}>
                              <FiToggleRight className="text-xs" />
                            </button>
                            {/* Open the form modal in EDIT mode for
                                this tenant. The pencil intentionally
                                lives next to the destructive trash
                                button so the two CRUD actions are
                                grouped visually. */}
                            <button
                              onClick={() => { setFormTenant(tenant); setFormOpen(true); }}
                              className="p-2 bg-gray-500/15 hover:bg-gray-500/30 text-gray-400 rounded-xl transition-colors"
                              title={t("admin_edit_tenant")}
                            >
                              <FiEdit2 className="text-xs" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(tenant)}
                              className="p-2 bg-red-500/15 hover:bg-red-500/30 text-red-400 rounded-xl transition-colors"
                              title={t("admin_delete_tenant")}
                            >
                              <FiTrash2 className="text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedTenants.has(tenant.id) && (
                        <tr key={`${tenant.id}-usage`} className={darkMode ? "bg-gray-900/40" : "bg-gray-50"}>
                          <td colSpan="8" className="px-6 py-4">
                            {usageLoadingFor === tenant.id && !tenantUsageMap[tenant.id] ? (
                              <div className={`flex items-center gap-2 ${subCls} text-sm`}><FiRefreshCw className="animate-spin" />{lang === "ar" ? "جارٍ تحميل الاستخدام…" : "Loading usage…"}</div>
                            ) : tenantUsageMap[tenant.id] ? (
                              <TenantUsageRow data={tenantUsageMap[tenant.id]} lang={lang} isRTL={isRTL} />
                            ) : (
                              <p className={`${subCls} text-sm`}>{lang === "ar" ? "لا تتوفر بيانات الاستخدام" : "Usage data unavailable"}</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {tenants.length === 0 && <tr><td colSpan="8" className={`p-12 text-center ${subCls}`}>{t("no_tenants")}</td></tr>}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* ══ TAB: USERS ══ */}
        {activeTab === "users" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`${card} overflow-hidden`}>
            <div className={`flex items-center justify-between p-5 border-b ${divider}`}>
              <h2 className={`font-semibold ${headingCls}`}>{t("users")} ({users.length})</h2>
              <button onClick={fetchData} className={refreshBtn}><FiRefreshCw className="text-sm" /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theadBg}>
                  <tr>
                    <th className={thCls}>{t("user")}</th>
                    <th className={thCls}>{t("col_company")}</th>
                    <th className={thCls}>{t("role")}</th>
                    <th className={thCls}>{t("col_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => {
                    const normRole = ROLE_META[normaliseRole(u.role)] ? normaliseRole(u.role) : "viewer";
                    const meta     = ROLE_META[normRole];
                    return (
                      <tr key={u.id} className={`border-t ${rowHover} transition-colors`}>
                        <td className="p-4">
                          <p className={`font-medium ${headingCls}`}>{u.displayName || "—"}</p>
                          <p className={`${subCls} text-xs`}>{u.email}</p>
                        </td>
                        <td className={`p-4 ${textCls} text-sm`}>{u.tenantName || "—"}</td>
                        <td className="p-4">
                          <select value={normRole}
                            onChange={async (e) => {
                              const newRole = e.target.value;
                              setActionLoading(`role-${u.id}`);
                              try { await httpsCallable(functions, "updateUserRole")({ userId: u.id, role: newRole }); setUsers(prev => prev.map(x => x.id === u.id ? { ...x, role: newRole } : x)); showToast(t("role_changed")); }
                              catch (err) { showToast(err.message || t("error"), "error"); }
                              finally { setActionLoading(null); }
                            }}
                            disabled={actionLoading === `role-${u.id}`}
                            className={`appearance-none px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer bg-transparent focus:outline-none ${meta?.cls || "bg-gray-500/20 text-gray-400 border-gray-500/40"}`}>
                            {Object.entries(ROLE_META).map(([val, m]) => (
                              <option key={val} value={val} className={darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"}>{t(m.labelKey)}</option>
                            ))}
                          </select>
                          {actionLoading === `role-${u.id}` && <FiRefreshCw className="animate-spin text-blue-400 text-xs ml-2 inline" />}
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${u.status === "active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                            {u.status === "active" ? t("status_active") : u.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 && <tr><td colSpan="4" className={`p-12 text-center ${subCls}`}>{t("no_users")}</td></tr>}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* ══ TAB: FEATURE FLAGS ══ */}
        {activeTab === "features" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <div className={`${card} p-4`}>
                <h3 className={`${headingCls} font-semibold mb-4 text-sm`}>{t("total_tenants")}</h3>
                <div className="space-y-1">
                  {tenants.map(tenant => (
                    <button key={tenant.id} onClick={() => handleSelectFeatureTenant(tenant)}
                      className={`w-full px-3 py-3 rounded-xl text-sm transition-colors text-left ${selectedTenant?.id === tenant.id ? "bg-blue-600 text-white" : darkMode ? "text-gray-300 hover:bg-gray-700/60" : "text-gray-600 hover:bg-gray-100"}`}>
                      <p className="font-medium">{tenant.name}</p>
                      <p className="text-xs opacity-60 capitalize mt-0.5">{tenant.planId || tenant.plan || "free"}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              {!selectedTenant ? (
                <div className={`${card} p-12 text-center`}>
                  <FiToggleRight className={`text-4xl mx-auto mb-3 opacity-30 ${subCls}`} />
                  <p className={subCls}>{t("select_tenant_prompt")}</p>
                </div>
              ) : featLoading ? (
                <div className={`${card} p-12 text-center`}><FiRefreshCw className="animate-spin text-blue-400 text-2xl mx-auto" /></div>
              ) : (
                <div className={`${card} p-6`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-left">
                      <h2 className={`text-lg font-bold ${headingCls}`}>{selectedTenant.name}</h2>
                      <p className={`${subCls} text-xs mt-0.5`}>
                        {lang === "ar" ? "الميزات تُحفظ تلقائياً عند التغيير" : "Flags auto-save on toggle"}
                        {savingFlag && <span className="ml-2 text-blue-400 animate-pulse">⟳ {lang === "ar" ? "جارٍ الحفظ…" : "Saving…"}</span>}
                      </p>
                    </div>
                    {savingFlag ? <FiRefreshCw className="animate-spin text-blue-400" /> : <FiCheckCircle className="text-green-400" />}
                  </div>
                  <FlagSection title={t("section_core")} darkMode={darkMode} divider={divider} headingCls={headingCls}>
                    <FeatureFlagToggle name="products_page_enabled"  label={t("products_page_enabled")} description={t("products_page_enabled_desc")} checked={features.products_page_enabled !== false} onChange={handleToggleFeature} />
                    <FeatureFlagToggle name="share_enabled"          label={t("share_links")}           description={t("share_links_desc")}           checked={features.share_enabled}           onChange={handleToggleFeature} />
                    <FeatureFlagToggle name="pdf_enabled"            label={t("download_pdf")}          description={t("pdf_export_desc")}            checked={features.pdf_enabled}             onChange={handleToggleFeature} />
                    <FeatureFlagToggle name="analytics_enabled"      label={t("analytics")}             description={t("analytics_desc")}             checked={features.analytics_enabled}       onChange={handleToggleFeature} />
                  </FlagSection>
                  <FlagSection title={t("allowed_modes")} darkMode={darkMode} divider={divider} headingCls={headingCls}>
                    <div className="md:col-span-2">
                      <FeatureFlagToggle name="decision_mode_enabled" label={t("mode_selection")} description={t("mode_selection_desc")} checked={features.decision_mode_enabled} onChange={handleToggleFeature} />
                    </div>
                    {features.decision_mode_enabled && (
                      <div className="md:col-span-2">
                        <p className={`${subCls} text-sm mb-3`}>{t("allowed_modes")}:</p>
                        <div className="flex gap-6">
                          {["conservative", "balanced", "aggressive"].map(mode => (
                            <label key={mode} className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={features.allowed_modes?.includes(mode)}
                                onChange={e => handleArrayFeature("allowed_modes", e.target.checked ? [...(features.allowed_modes || []), mode] : features.allowed_modes?.filter(m => m !== mode))}
                                className="rounded" />
                              <span className={`${textCls} text-sm capitalize`}>{t(`decision_mode_${mode}`)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </FlagSection>
                  <FlagSection title={t("enabled_languages")} darkMode={darkMode} divider={divider} headingCls={headingCls}>
                    <div className="md:col-span-2">
                      <div className="flex gap-6">
                        {[{ code: "en", label: "English" }, { code: "ar", label: "العربية" }].map(lng => (
                          <label key={lng.code} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={features.languages_enabled?.includes(lng.code)}
                              onChange={e => handleArrayFeature("languages_enabled", e.target.checked ? [...(features.languages_enabled || []), lng.code] : features.languages_enabled?.filter(l => l !== lng.code))}
                              className="rounded" />
                            <span className={`${textCls} text-sm`}>{lng.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </FlagSection>
                  <FlagSection title={t("max_users")} darkMode={darkMode} divider={divider} headingCls={headingCls}>
                    <div>
                      <label className={`block ${subCls} text-sm mb-2`}>{t("max_users")}</label>
                      <input type="number" min="1" max="1000" value={features.max_users || 5} onChange={e => handleMaxUsers(e.target.value)} className={inputCls} />
                    </div>
                    <FeatureFlagToggle name="api_access_enabled" label={t("api_access")} description={t("api_access_desc")} checked={features.api_access_enabled} onChange={handleToggleFeature} />
                  </FlagSection>
                  <FlagSection title={t("advanced_features")} darkMode={darkMode} divider={divider} headingCls={headingCls}>
                    <FeatureFlagToggle name="export_enabled"         label={t("data_export")}     description={t("data_export_desc")}     checked={features.export_enabled}         onChange={handleToggleFeature} />
                    <FeatureFlagToggle name="custom_branding_enabled" label={t("custom_branding")} description={t("custom_branding_desc")} checked={features.custom_branding_enabled} onChange={handleToggleFeature} />
                  </FlagSection>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ══ TAB: PLANS ══ */}
        {activeTab === "plans" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {planLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-center space-y-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mx-auto" />
                  <p className={`${subCls} text-sm`}>{t("loading_plans")}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* The Current Usage card only makes sense for tenant
                    owners. System admins don't belong to a tenant, so
                    `getTenantUsageStats({tenantId: undefined})` would
                    error out and we'd render bogus data. We only show
                    the plan cards + help section for them. */}
                {planUsage && currentPlan && currentUser?.tenantId && (
                  <div className={`${card} p-6`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className={`text-lg font-bold ${headingCls}`}>{t("current_usage")}</h2>
                        <p className={`${subCls} text-sm`}>{t("current_plan")}: <span className="text-blue-400 font-medium">{currentPlan.name}</span></p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${headingCls}`}>
                          {currentPlan.price}{" "}
                          {/* Currency aware of language: SAR for English,
                              ر.س for Arabic. The previous "$" prefix was
                              incorrect for the Saudi/GCC market this app
                              targets. */}
                          <span className="text-base font-normal">{lang === "ar" ? "ر.س" : "SAR"}</span>
                        </p>
                        <p className={`${subCls} text-sm`}>{t("per_month")}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <UsageProgress label={t("evaluations")}    current={planUsage.evaluations?.current || 0} quota={planUsage.evaluations?.quota || 0} unit={t("this_month")} showWarning />
                      <UsageProgress label={t("total_campaigns")} current={planUsage.campaigns?.current || 0}  quota={planUsage.campaigns?.quota || 0}   unit={t("total")}      showWarning />
                      <UsageProgress label={t("share_links")}    current={planUsage.shareLinks?.current || 0} quota={planUsage.shareLinks?.quota || 0}  unit={t("total")}      showWarning />
                      <UsageProgress label={t("pdfs_generated")} current={planUsage.pdf?.current || 0}        quota={planUsage.pdf?.quota || 0}          unit={t("this_month")} showWarning />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {plans.map(plan => <PlanCard key={plan.id} plan={plan} currentPlan={currentPlan} onSelectPlan={handleSelectPlan} loading={updatingPlan} featured={plan.id === "pro"} />)}
                </div>
                <div className={`${card} p-6`}>
                  <h3 className={`text-lg font-bold ${headingCls} mb-4`}>{t("need_help_choosing")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { title: t("free_plan_name"), desc: t("free_plan_desc") },
                      { title: t("pro_plan_name"),  desc: t("pro_plan_desc") },
                      { title: t("enterprise_plan_name"), desc: t("enterprise_plan_desc") },
                      { title: t("questions"), desc: <>{t("contact_support")} <a href="mailto:support@nexusflow.io" className="text-blue-400 hover:underline">support@nexusflow.io</a></> },
                    ].map((item, i) => (
                      <div key={i}>
                        <h4 className={`font-semibold ${headingCls} mb-1 text-sm`}>{item.title}</h4>
                        <p className={`${subCls} text-xs leading-relaxed`}>{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                  <p className="text-blue-400 text-sm"><FiZap className="inline mr-2" /><strong>{t("note")}:</strong> {t("mvp_manual_billing")}</p>
                </div>
              </div>
            )}
          </motion.div>
        )}

      </div>

      {/* ── Tenant CRUD modals ─────────────────────────────
          Mounted at the root of the component so they overlay
          all tabs. They control their own visibility via the
          `open` prop. */}
      <TenantFormModal
        open={formOpen}
        tenant={formTenant}
        onClose={() => setFormOpen(false)}
        onSaved={({ mode }) => {
          // Refetch and toast. We don't try to surgically update the
          // local tenants array — fetchData is cheap (single Firestore
          // read) and gives us a guaranteed-consistent view.
          fetchData();
          showToast(mode === "create" ? t("admin_tenant_created") : t("admin_tenant_updated"));
        }}
      />
      <DeleteTenantDialog
        open={!!deleteTarget}
        tenant={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          fetchData();
          showToast(t("admin_tenant_deleted"));
        }}
      />
    </div>
  );
};

/* ── Tenant Usage Sub-Row ────────────────────────────────── */
const TenantUsageRow = ({ data, lang, isRTL }) => {
  const { darkMode } = useDarkMode();
  const { usage, plan } = data;
  const METRICS = [
    { key: "evaluations", labelEN: "Evaluations",  labelAR: "التقييمات",      color: "blue"   },
    { key: "campaigns",   labelEN: "Campaigns",    labelAR: "الحملات",        color: "green"  },
    { key: "shareLinks",  labelEN: "Share Links",  labelAR: "روابط المشاركة", color: "purple" },
    { key: "pdf",         labelEN: "PDFs",         labelAR: "ملفات PDF",      color: "orange" },
  ];
  const COLOR_MAP = {
    blue:   { bar: "bg-blue-500",   text: "text-blue-400",   bg: "bg-blue-500/10"   },
    green:  { bar: "bg-green-500",  text: "text-green-400",  bg: "bg-green-500/10"  },
    purple: { bar: "bg-purple-500", text: "text-purple-400", bg: "bg-purple-500/10" },
    orange: { bar: "bg-orange-500", text: "text-orange-400", bg: "bg-orange-500/10" },
  };
  return (
    <div>
      {plan && (
        <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs mb-3`}>
          {lang === "ar" ? "الخطة الحالية:" : "Current plan:"}{" "}
          <span className="text-blue-400 font-semibold">{plan.name}</span>
          {plan.price !== undefined && (
            <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} ml-2`}>
              {/* Currency: SAR (en) / ر.س (ar). The "$" was left over
                  from when prices were USD-denominated; this app is now
                  Saudi Riyal across the board. */}
              ({plan.price} {lang === "ar" ? "ر.س" : "SAR"}/{lang === "ar" ? "شهر" : "mo"})
            </span>
          )}
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {METRICS.map(m => {
          const stat = usage?.[m.key];
          if (!stat) return null;
          // Hide metrics that the plan does not include at all (quota
          // 0 = "this plan doesn't get this feature"). Showing "0 / 0"
          // is misleading — better to omit the card. -1 stays visible
          // because it means "unlimited" and is rendered as ∞.
          if (stat.quota === 0) return null;
          const isUnlimited = stat.quota === -1 || stat.quota === 999999;
          const pct = isUnlimited ? 0 : Math.min(parseFloat(stat.percentage || 0), 100);
          const c   = COLOR_MAP[m.color];
          const isExceeded = stat.warning === "exceeded";
          const isWarning  = stat.warning === "warning";
          const quota = isUnlimited ? "∞" : stat.quota;
          return (
            <div key={m.key} className={`${c.bg} ${darkMode ? "border-gray-700" : "border-gray-200"} border rounded-xl p-3`}>
              <div className={`flex items-center justify-between mb-2 ${isRTL ? "flex-row-reverse" : ""}`}>
                <span className={`${darkMode ? "text-gray-400" : "text-gray-600"} text-xs`}>{lang === "ar" ? m.labelAR : m.labelEN}</span>
                {isExceeded && <span className="text-red-400 text-xs font-bold">⚠️</span>}
                {isWarning  && <span className="text-yellow-400 text-xs">⚡</span>}
              </div>
              <p className={`text-lg font-bold ${c.text}`}>{stat.current}<span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs font-normal`}> / {quota}</span></p>
              {/* Progress bar + percentage are meaningless for unlimited
                  plans, so we hide them. The ∞ in the quota line above
                  already conveys the state. */}
              {!isUnlimited && (
                <>
                  <div className={`w-full ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded-full h-1.5 mt-2`}>
                    <div className={`h-1.5 rounded-full transition-all ${isExceeded ? "bg-red-500" : isWarning ? "bg-yellow-500" : c.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1 text-right`}>{pct.toFixed(0)}%</p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Section wrapper ─────────────────────────────────────── */
const FlagSection = ({ title, children, darkMode, divider, headingCls }) => (
  <div className="mb-6">
    <h3 className={`text-sm font-semibold mb-4 pb-2 border-b ${headingCls} ${divider}`}>{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

export default AdminDashboard;